import { useState } from "react";
import { wabaApi } from "../../lib/wabaApi";

// Conecta a conta da loja no Mercado Livre ou na OLX (OAuth) ou na Webmotors (página
// nossa: e-mail + senha do Integrador de API). Quem conecta é o lojista (no ML, com a
// conta ADMINISTRADORA): "Copiar link" manda pra ele (vale 7 dias, uso único);
// "Abrir agora" quando a equipe está junto dele.

const INFO = {
  meli: { nome: "Mercado Livre", conta: "a conta principal do ML" },
  olx: { nome: "OLX", conta: "a conta da loja na OLX" },
  webmotors: { nome: "Webmotors", conta: "o e-mail e a senha do usuário Integrador de API (criado no Cockpit)" },
};

interface Props {
  connectionId: string;
  provider: keyof typeof INFO;
}

const btn = (primary: boolean): React.CSSProperties => ({
  padding: "7px 14px", borderRadius: 8, fontSize: 13, fontWeight: 600, cursor: "pointer",
  border: primary ? "none" : "1px solid var(--line-strong)",
  background: primary ? "var(--brand)" : "transparent",
  color: primary ? "var(--surface-card)" : "var(--ink)",
});

// Teste do canal ML: o que o Mercado Livre tem desta loja (leads de ontem/hoje e
// notificações que ele não conseguiu entregar). ponytail: sai quando estabilizar.
export function MeliDiagnostico({ connectionId }: { connectionId: string }) {
  const [out, setOut] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  async function run() {
    setLoading(true);
    try {
      setOut(JSON.stringify(await wabaApi.get(`/connections/${connectionId}/meli/diagnostico`), null, 2));
    } catch (err: any) {
      setOut(err?.payload?.detail ?? "Erro ao consultar o Mercado Livre.");
    } finally { setLoading(false); }
  }

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
      <button onClick={run} disabled={loading} style={{ ...btn(false), alignSelf: "flex-start" }}>
        {loading ? "Consultando…" : "Testar recebimento (consulta o ML)"}
      </button>
      {out && (
        <pre style={{
          margin: 0, maxHeight: 320, overflow: "auto", fontSize: 11, padding: 10, borderRadius: 8,
          background: "var(--surface-card)", border: "1px solid var(--line)",
          whiteSpace: "pre-wrap", wordBreak: "break-word",
        }}>{out}</pre>
      )}
    </div>
  );
}

// "Ver log da integração": as linhas olx_/meli_/webmotors_ desta conexão (de qualquer réplica
// e do worker), mais nova primeiro — pra copiar e mandar, sem garimpar o Portainer.
export function IntegracaoLog({ connectionId, canal }: { connectionId: string; canal: "mercadolivre" | "olx" | "webmotors" | "email" }) {
  const [linhas, setLinhas] = useState<{ t: number; nivel: string; msg: string }[] | null>(null);
  const [erro, setErro] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  async function carregar() {
    setLoading(true); setErro(null);
    try {
      const r = await wabaApi.get<{ linhas: { t: number; nivel: string; msg: string }[] }>(
        `/connections/${connectionId}/integracao/log?canal=${canal}`);
      setLinhas(r.linhas);
    } catch (err: any) {
      setErro(err?.payload?.detail ?? "Erro ao carregar o log.");
    } finally { setLoading(false); }
  }

  const hora = (t: number) => new Date(t).toLocaleString("pt-BR", { timeZone: "America/Sao_Paulo",
    day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit", second: "2-digit" });

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
      <button onClick={carregar} disabled={loading} style={{ ...btn(false), alignSelf: "flex-start" }}>
        {loading ? "Carregando…" : linhas ? "Atualizar log da integração" : "Ver log da integração"}
      </button>
      {erro && <span style={{ fontSize: 12, color: "var(--danger)" }}>{erro}</span>}
      {linhas && (
        <pre style={{
          margin: 0, maxHeight: 360, overflow: "auto", fontSize: 11, padding: 10, borderRadius: 8,
          background: "var(--surface-card)", border: "1px solid var(--line)",
          whiteSpace: "pre-wrap", wordBreak: "break-word",
        }}>
          {linhas.length
            ? linhas.map(l => `${hora(l.t)}  ${l.nivel === "INFO" ? "" : l.nivel + "  "}${l.msg}`).join("\n\n")
            : "Nada registrado nos últimos 3 dias."}
        </pre>
      )}
    </div>
  );
}

// Dados que o suporte da OLX pede p/ cadastrar o recebimento de leads à mão.
export function OlxLeadsConfig({ connectionId }: { connectionId: string }) {
  const [dados, setDados] = useState<string | null>(null);
  async function carregar() {
    try {
      const r = await wabaApi.get<{ email: string | null; url: string; token: string }>(
        `/connections/${connectionId}/olx/leads-config`);
      setDados(`E-mail da conta OLX: ${r.email ?? "—"}
Endpoint para receber os leads: ${r.url}`);
    } catch (err: any) {
      setDados(err?.payload?.detail ?? "Erro ao carregar os dados.");
    }
  }
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
      <button onClick={carregar} style={{ ...btn(false), alignSelf: "flex-start" }}>Dados para a OLX (leads)</button>
      {dados && (
        <pre style={{ margin: 0, fontSize: 11, padding: 10, borderRadius: 8, background: "var(--surface-card)",
          border: "1px solid var(--line)", whiteSpace: "pre-wrap", wordBreak: "break-all" }}>{dados}</pre>
      )}
    </div>
  );
}

export function ClassificadoConnect({ connectionId, provider }: Props) {
  const info = INFO[provider];
  const [loading, setLoading] = useState(false);
  const [msg, setMsg] = useState<{ ok: boolean; text: string } | null>(null);

  async function link(): Promise<string | null> {
    setLoading(true); setMsg(null);
    try {
      const r = await wabaApi.post<{ url: string }>(`/connections/${connectionId}/${provider}/link`);
      return r.url;
    } catch (err: any) {
      setMsg({ ok: false, text: err?.payload?.detail ?? `Erro ao gerar o link da conexão (${info.nome}).` });
      return null;
    } finally { setLoading(false); }
  }

  async function abrir() {
    // janela aberta NO clique (antes do await): Safari/bloqueador recusa pop-up aberto depois
    const win = window.open("", "_blank");
    const url = await link();
    if (url && win) win.location.href = url;
    else if (url) window.location.href = url;
    else win?.close();
  }

  async function copiar() {
    const url = await link();
    if (!url) return;
    try {
      await navigator.clipboard.writeText(url);
      setMsg({ ok: true, text: `Link copiado. Vale 7 dias e uma conexão; o lojista entra com ${info.conta}.` });
    } catch {
      setMsg({ ok: true, text: `Não deu para copiar automaticamente. Copie o link: ${url}` });
    }
  }

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
      <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
        <button onClick={copiar} disabled={loading} style={btn(true)}>Copiar link</button>
        <button onClick={abrir} disabled={loading} style={btn(false)}>Abrir agora</button>
      </div>
      {msg && <span style={{ fontSize: 12, color: msg.ok ? "var(--ok)" : "var(--danger)" }}>{msg.text}</span>}
    </div>
  );
}
